import React from "react";
import { Box, Chip, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

type KitchenArrivalRow = {
  numShots?: number | null;
};

type ShotStats = {
  min: number;
  max: number;
  mean: number;
  median: number;
  count: number;
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function medianOfSorted(nums: number[]): number {
  const n = nums.length;
  if (!n) return 0;
  const mid = Math.floor(n / 2);
  if (n % 2 === 1) return nums[mid]!;
  return (nums[mid - 1]! + nums[mid]!) / 2;
}

function computeStats(rows: KitchenArrivalRow[]): ShotStats {
  const vals = rows
    .map((r) => (typeof r.numShots === "number" ? r.numShots : null))
    .filter((x): x is number => typeof x === "number" && Number.isFinite(x))
    .sort((a, b) => a - b);

  const count = vals.length;
  if (!count) return { min: 0, max: 0, mean: 0, median: 0, count: 0 };

  const min = vals[0]!;
  const max = vals[vals.length - 1]!;
  const sum = vals.reduce((acc, x) => acc + x, 0);
  const mean = sum / count;
  const median = medianOfSorted(vals);

  return { min, max, mean, median, count };
}

type HistBin = { shots: number; count: number };

function buildHistogram(
  rows: KitchenArrivalRow[],
  minShots: number,
  maxShots: number
): HistBin[] {
  const counts = new Map<number, number>();
  for (const r of rows) {
    const n = typeof r.numShots === "number" ? r.numShots : null;
    if (n == null || !Number.isFinite(n)) continue;
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }

  const bins: HistBin[] = [];
  for (let s = minShots; s <= maxShots; s++) {
    bins.push({ shots: s, count: counts.get(s) ?? 0 });
  }
  return bins;
}

function RangeHandle(props: {
  side: "min" | "max";
  leftPx: number;
  onPointerDown: (e: React.PointerEvent) => void;
  dragging: boolean;
}) {
  return (
    <Box
      onPointerDown={props.onPointerDown}
      title="Drag to adjust shots filter"
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: `${props.leftPx - 10}px`, // center hitbox
        width: 20,
        cursor: "ew-resize",
        zIndex: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .handleCore": {
          opacity: props.dragging ? 1 : 0.85,
          transform: props.dragging ? "scale(1.05)" : "scale(1)",
          transition: "opacity 80ms linear, transform 80ms linear",
        },
        "&:hover .handleCore": { opacity: 1 },
      }}
    >
      <Box
        className="handleCore"
        sx={{
          width: 10,
          height: 44,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(0,0,0,0.28)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 0,
            height: 0,
            borderTop: "5px solid transparent",
            borderBottom: "5px solid transparent",
            borderRight:
              props.side === "min"
                ? "6px solid rgba(0,0,0,0.45)"
                : "0px solid transparent",
            borderLeft:
              props.side === "max"
                ? "6px solid rgba(0,0,0,0.45)"
                : "0px solid transparent",
          }}
        />
      </Box>
    </Box>
  );
}

function rangeForClickedValue(
  n: number,
  min: number,
  max: number
): [number, number] {
  if (min >= max) return [n, n]; // degenerate case
  if (n >= max) return [max - 1, max];
  if (n <= min) return [min, min + 1];
  return [n, n + 1];
}

function widenSingletonRange(
  a: number,
  b: number,
  min: number,
  max: number
): [number, number] {
  if (a !== b) return [a, b];

  if (a < max) return [a, a + 1];
  if (a > min) return [a - 1, a];

  return [a, b];
}

export function ShotsHistogramFilter(props: {
  rows: KitchenArrivalRow[];

  range: [number, number];
  onChangeRange: (next: [number, number]) => void;

  title?: string;

  // sticky top bar container
  containerRef: React.RefObject<HTMLElement | null>;
}) {
  const { rows, range, onChangeRange, title = "Shots", containerRef } = props;

  // must match your BarChart margin + YAxis width
  const PLOT_INSET_LEFT = 34 + 8; // YAxis width + BarChart margin.left
  const PLOT_INSET_RIGHT = 10; // BarChart margin.right

  // Layout
  const RIGHT_PAD = 16;
  const PANEL_W = 460;
  const PANEL_H = 156; // reserved height so it won't overlap content below

  const theme = useTheme();
  const inFill = theme.palette.primary.main;
  const outFill = theme.palette.action.disabledBackground;

  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const chartWrapRef = React.useRef<HTMLDivElement | null>(null);

  const stats = React.useMemo(() => computeStats(rows), [rows]);
  const minShots = stats.min;
  const maxShots = stats.max;

  const safeRange: [number, number] = React.useMemo(() => {
    const a = clamp(range[0], minShots, maxShots);
    const b = clamp(range[1], minShots, maxShots);
    return a <= b ? [a, b] : [b, a];
  }, [range, minShots, maxShots]);

  const bins = React.useMemo(
    () => buildHistogram(rows, minShots, maxShots),
    [rows, minShots, maxShots]
  );

  const [uiRange, setUiRange] = React.useState<[number, number]>(safeRange);
  const uiRangeRef = React.useRef<[number, number]>(safeRange);

  React.useEffect(() => {
    uiRangeRef.current = uiRange;
  }, [uiRange]);

  // -----------------------------
  // Right-align (no dragging)
  // -----------------------------
  const [x, setX] = React.useState(0);

  React.useLayoutEffect(() => {
    const c = containerRef.current;
    const p = panelRef.current;
    if (!c || !p) return;

    const positionRight = () => {
      const cW = c.getBoundingClientRect().width;
      const pW = p.getBoundingClientRect().width;
      const rightX = Math.max(0, cW - pW - RIGHT_PAD);
      setX(rightX);
    };

    positionRight();

    const ro = new ResizeObserver(() => positionRight());
    ro.observe(c);
    ro.observe(p);

    window.addEventListener("resize", positionRight);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", positionRight);
    };
  }, [containerRef]);

  // -----------------------------
  // The key: UI range vs committed
  // -----------------------------
  const draggingRef = React.useRef(false);
  //   const [uiRange, setUiRange] = React.useState<[number, number]>(safeRange);

  // When parent changes range (e.g. reset filters), sync UI range,
  // but don't fight the user's drag.
  React.useEffect(() => {
    if (draggingRef.current) return;
    setUiRange(safeRange);
    uiRangeRef.current = safeRange;
  }, [safeRange]);

  const uiMin = uiRange[0];
  const uiMax = uiRange[1];

  // Debounced commit while dragging
  //   const commitTimerRef = React.useRef<number | null>(null);
  //   const pendingCommitRef = React.useRef<[number, number] | null>(null);

  const commitNow = React.useCallback(
    (next: [number, number]) => {
      onChangeRange(next);
    },
    [onChangeRange]
  );

  //   const commitNow_old = React.useCallback(
  //     (next: [number, number]) => {
  //       if (commitTimerRef.current != null) {
  //         window.clearTimeout(commitTimerRef.current);
  //         commitTimerRef.current = null;
  //       }
  //       pendingCommitRef.current = null;
  //       onChangeRange(next);
  //     },
  //     [onChangeRange]
  //   );

  //   const scheduleCommit = React.useCallback(
  //     (next: [number, number]) => {
  //       pendingCommitRef.current = next;

  //       if (commitTimerRef.current != null) return;

  //       // <- tune this. 80–150ms is a good “feels instant” range.
  //       commitTimerRef.current = window.setTimeout(() => {
  //         commitTimerRef.current = null;
  //         const p = pendingCommitRef.current;
  //         pendingCommitRef.current = null;
  //         if (p) onChangeRange(p);
  //       }, 120);
  //     },
  //     [onChangeRange]
  //   );

  //   React.useEffect(() => {
  //     return () => {
  //       if (commitTimerRef.current != null)
  //         window.clearTimeout(commitTimerRef.current);
  //     };
  //   }, []);

  // -----------------------------
  // Drag mechanics
  // -----------------------------
  type DragMode = null | "range" | "min" | "max";
  const [dragMode, setDragMode] = React.useState<DragMode>(null);
  const activePointerIdRef = React.useRef<number | null>(null);
  const dragStartShotRef = React.useRef<number | null>(null);
  const dragStartClientXRef = React.useRef<number | null>(null);

  const shotFromClientX = React.useCallback(
    (clientX: number): number | null => {
      const el = chartWrapRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();

      const plotW = r.width - PLOT_INSET_LEFT - PLOT_INSET_RIGHT;
      if (plotW <= 0) return null;

      const xPlot = clientX - r.left - PLOT_INSET_LEFT;
      const t = clamp(xPlot / plotW, 0, 1);

      const shot = Math.round(minShots + t * (maxShots - minShots));
      return clamp(shot, minShots, maxShots);
    },
    [minShots, maxShots]
  );

  const shotToLeftPx = React.useCallback(
    (shot: number): number => {
      const el = chartWrapRef.current;
      if (!el) return PLOT_INSET_LEFT;

      const w = el.getBoundingClientRect().width;
      const plotW = Math.max(1, w - PLOT_INSET_LEFT - PLOT_INSET_RIGHT);

      const denom = Math.max(1, maxShots - minShots);
      const t = (shot - minShots) / denom;

      return PLOT_INSET_LEFT + clamp(t, 0, 1) * plotW;
    },
    [minShots, maxShots]
  );

  const setUiRangeClamped = React.useCallback(
    (a: number, b: number) => {
      const lo = clamp(Math.min(a, b), minShots, maxShots);
      const hi = clamp(Math.max(a, b), minShots, maxShots);
      const next: [number, number] = [lo, hi];
      uiRangeRef.current = next;
      setUiRange(next);
      //   scheduleCommit(next); // throttle the expensive parent update
    },
    [minShots, maxShots] //, scheduleCommit]
  );

  React.useEffect(() => {
    if (!dragMode) return;

    const onMove = (e: PointerEvent) => {
      const pid = activePointerIdRef.current;
      if (pid != null && e.pointerId !== pid) return;

      const s = shotFromClientX(e.clientX);
      if (s == null) return;

      if (dragMode === "range") {
        const start = dragStartShotRef.current;
        if (start == null) return;
        setUiRangeClamped(start, s);
        return;
      }

      if (dragMode === "min") {
        setUiRangeClamped(Math.min(s, uiMax), uiMax);
        return;
      }

      if (dragMode === "max") {
        setUiRangeClamped(uiMin, Math.max(s, uiMin));
        return;
      }
    };

    const onUp = (e: PointerEvent) => {
      const pid = activePointerIdRef.current;
      if (pid != null && e.pointerId !== pid) return;

      const startShot = dragStartShotRef.current;
      const startX = dragStartClientXRef.current;
      const moved = startX != null ? Math.abs(e.clientX - startX) : 0;

      draggingRef.current = false;

      // Click (tiny movement) => singleton
      if (dragMode === "range" && startShot != null && moved < 5) {
        const next: [number, number] = [startShot, startShot];
        uiRangeRef.current = next;
        setUiRange(next);
        commitNow(next);
      } else {
        // Drag => commit whatever the UI ended on
        commitNow(uiRangeRef.current);
      }

      setDragMode(null);
      activePointerIdRef.current = null;
      dragStartShotRef.current = null;
      dragStartClientXRef.current = null;
    };

    const onUp_old = (e: PointerEvent) => {
      const pid = activePointerIdRef.current;
      if (pid != null && e.pointerId !== pid) return;

      const startShot = dragStartShotRef.current;
      const startX = dragStartClientXRef.current;
      const moved = startX != null ? Math.abs(e.clientX - startX) : 0;

      draggingRef.current = false;

      if (dragMode === "range" && startShot != null && moved < 5) {
        const next: [number, number] = [startShot, startShot];
        setUiRange(next);
        commitNow(next);
      } else {
        // flush last pending immediately
        commitNow([uiMin, uiMax]);
      }

      setDragMode(null);
      activePointerIdRef.current = null;
      dragStartShotRef.current = null;
      dragStartClientXRef.current = null;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("pointercancel", onUp, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragMode, shotFromClientX, setUiRangeClamped, uiMin, uiMax, commitNow]);

  // -----------------------------
  // Render
  // -----------------------------
  //   const leftMinPx = shotToLeftPx(uiMin);
  //   const leftMaxPx = shotToLeftPx(uiMax);
  //   const bandLeft = Math.min(leftMinPx, leftMaxPx);
  //   const bandW = Math.max(0, Math.abs(leftMaxPx - leftMinPx));

  const leftMinPxRaw = shotToLeftPx(uiMin);
  const leftMaxPxRaw = shotToLeftPx(uiMax);

  // Force a minimum pixel separation between handles (visual only)
  const MIN_HANDLE_GAP_PX = 26;

  let leftMinPx = leftMinPxRaw;
  let leftMaxPx = leftMaxPxRaw;

  if (uiMin === uiMax) {
    // committed singleton: show two handles separated visually around the same bar
    leftMinPx = leftMinPxRaw - MIN_HANDLE_GAP_PX / 2;
    leftMaxPx = leftMaxPxRaw + MIN_HANDLE_GAP_PX / 2;
  } else {
    const gap = leftMaxPxRaw - leftMinPxRaw;
    if (gap > 0 && gap < MIN_HANDLE_GAP_PX) {
      leftMaxPx = leftMinPxRaw + MIN_HANDLE_GAP_PX;
    } else if (gap < 0 && -gap < MIN_HANDLE_GAP_PX) {
      leftMinPx = leftMaxPxRaw + MIN_HANDLE_GAP_PX;
    }
  }

  const bandLeft = Math.min(leftMinPx, leftMaxPx);
  const bandW = Math.max(0, Math.abs(leftMaxPx - leftMinPx));
  const isDragging = dragMode != null;

  return (
    <Box sx={{ position: "relative", height: PANEL_H, width: PANEL_W }}>
      <Box
        ref={panelRef}
        sx={{
          //   position: "absolute",
          //   top: 0,
          //   left: 0,
          //   transform: `translateX(${Math.round(x)}px)`,
          width: PANEL_W,
          height: PANEL_H,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.14)",
          bgcolor: "background.paper",
          boxShadow: 1,
          p: 1,
          userSelect: "none",
          zIndex: 30,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.75,
          }}
        >
          <Box sx={{ fontWeight: 900, fontSize: 12 }}>{title}</Box>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Chip size="small" label={`min ${stats.min}`} sx={{ height: 20 }} />
            <Chip size="small" label={`max ${stats.max}`} sx={{ height: 20 }} />
            <Chip
              size="small"
              label={`median ${stats.median.toFixed(0)}`}
              sx={{ height: 20 }}
            />
            <Chip
              size="small"
              label={`mean ${stats.mean.toFixed(1)}`}
              sx={{ height: 20 }}
            />
          </Box>
        </Box>

        <Box
          ref={chartWrapRef}
          sx={{
            width: "100%",
            height: 110,
            position: "relative",
            cursor: isDragging ? "grabbing" : "crosshair",
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;

            const s = shotFromClientX(e.clientX);
            if (s == null) return;

            draggingRef.current = true;
            activePointerIdRef.current = e.pointerId;

            setDragMode("range");
            dragStartShotRef.current = s;
            dragStartClientXRef.current = e.clientX;

            const next: [number, number] = [s, s];
            uiRangeRef.current = next;
            setUiRange(next);

            try {
              (e.currentTarget as any).setPointerCapture?.(e.pointerId);
            } catch {}

            e.preventDefault();
          }}

          //   onPointerDown={(e) => {
          //     if (e.button !== 0) return;

          //     const s = shotFromClientX(e.clientX);
          //     if (s == null) return;

          //     draggingRef.current = true;
          //     activePointerIdRef.current = e.pointerId;

          //     setDragMode("range");
          //     dragStartShotRef.current = s;
          //     dragStartClientXRef.current = e.clientX;

          //     // immediate local feedback
          //     const next: [number, number] = [s, s];

          //     const logical: [number, number] = [s, s];
          //     const visual = widenSingletonRange(s, s, minShots, maxShots);

          //     setUiRange(visual); //next);

          //     // // don't spam parent; do a quick scheduled commit instead
          //     // scheduleCommit(logical); // next);

          //     try {
          //       (e.currentTarget as any).setPointerCapture?.(e.pointerId);
          //     } catch {}

          //     e.preventDefault();
          //   }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={bins}
              margin={{ top: 6, right: 10, left: 8, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="shots"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickFormatter={(v: any) => String(v)}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                width={34}
                allowDecimals={false}
                label={{
                  value: "Rallies",
                  angle: -90,
                  position: "insideLeft",
                  dy: 18,
                }}
              />
              <Tooltip
                formatter={(value: any) => [value, "rallies"]}
                isAnimationActive={false}
                active={isDragging ? false : undefined}
                labelFormatter={(label: any) => `shots: ${label}`}
              />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {bins.map((b) => {
                  const inRange = b.shots >= uiMin && b.shots <= uiMax;
                  return (
                    <Cell key={b.shots} fill={inRange ? inFill : outFill} />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* selection band (NOW updates instantly because it uses uiRange) */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: bandLeft,
              width: bandW,
              pointerEvents: "none",
              bgcolor: isDragging ? "rgba(0,0,0,0.09)" : "rgba(0,0,0,0.06)",
              transition: isDragging ? "none" : "background-color 120ms linear",
            }}
          />

          <RangeHandle
            side="min"
            leftPx={leftMinPx}
            dragging={dragMode === "min"}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();

              draggingRef.current = true;
              activePointerIdRef.current = e.pointerId;
              dragStartClientXRef.current = e.clientX;

              setDragMode("min");
              try {
                (e.currentTarget as any).setPointerCapture?.(e.pointerId);
              } catch {}
            }}
          />

          <RangeHandle
            side="max"
            leftPx={leftMaxPx}
            dragging={dragMode === "max"}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();

              draggingRef.current = true;
              activePointerIdRef.current = e.pointerId;
              dragStartClientXRef.current = e.clientX;

              setDragMode("max");
              try {
                (e.currentTarget as any).setPointerCapture?.(e.pointerId);
              } catch {}
            }}
          />
        </Box>

        <Box sx={{ mt: 0.5, fontSize: 12, opacity: 0.8 }}>
          Shots Filter: {uiMin}–{uiMax}
          {isDragging ? (
            <Box component="span" sx={{ ml: 1, fontSize: 12, opacity: 0.7 }}>
              (dragging…)
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
